import { gql } from '@apollo/client/core'
import { Notify } from 'quasar'
import { isElectronMode, isKds, isPos } from 'src/config'

class PrintTransactionsService {
  commands = {}

  constructor (apolloClient) {
    this.apolloClient = apolloClient

    this.checkForPrintTransactions = true
    this.orderId = null
    this.callback = null
    this.fetchingPrintTransactions = false
  }

  async connect () {
    const subQuery = gql`subscription {
      newPrintTransactions {
        printTransactions {
          type
          receiptData
          placeOrderItemIds
          voidOrderItemIds
        },
        orderId
      }
    }`

    const observer = this.apolloClient.subscribe({
      query: subQuery
    })

    observer.subscribe({
      next: data => {
        this.checkForPrintTransactions = true
        this.orderId = data?.data?.newPrintTransactions?.orderId || null

        if (!this.fetchingPrintTransactions) {
          this.fetchPrintTransactions()
        }
      },
      error: error => console.log(`new print transactions received error ${error}`)
    })
  }

  async fetchPrintTransactions () {
    const orderId = this.orderId
    const callback = this.callback

    this.fetchingPrintTransactions = true
    this.checkForPrintTransactions = false
    this.orderId = null
    this.callback = null

    this.apolloClient.query({
      ...(orderId
        ? {
            query: gql`query ($orderId: ID!) {
          printPickPrepOrder(orderId: $orderId) {
            type
            receiptData
            placeOrderItemIds
            voidOrderItemIds
          }
        }`,
            variables: {
              orderId
            }
          }
        : {
            query: gql`query {
          printTransactions {
            type
            receiptData
            placeOrderItemIds
            voidOrderItemIds
          }
        }`
          }),
      fetchPolicy: 'network-only'
    }).then(async (data) => {
      await this.handlePrintTransactions(data?.data?.printPickPrepOrder || data?.data?.printTransactions)
    }).catch((error) => {
      console.log('Error while handling print transactions: ', error.message)
    }).finally(() => {
      this.fetchingPrintTransactions = false
      callback?.()
    })

    if (this.checkForPrintTransactions) {
      await this.fetchPrintTransactions()
    }
  }

  printPickPrep (orderId, callback = null) {
    this.orderId = orderId
    this.callback = callback
    this.fetchPrintTransactions()
  }

  async handlePrintTransactions (printTransactions) {
    _.each(printTransactions, async (printTransaction) => {
      try {
        const placedPrintedIds = getPrintedIds(true)
        const voidedPrintedIds = getPrintedIds(false)

        let skipPrinting = false
        const itemsPlacedPrinted = placedPrintedIds.filter(printedId => printTransaction.placeOrderItemIds.includes(printedId))
        const itemsVoidedPrinted = voidedPrintedIds.filter(printedId => printTransaction.voidOrderItemIds.includes(printedId))

        // Skip printing if we find ids in local storage (this means we have printed but not told the API yet)
        skipPrinting = itemsPlacedPrinted.length !== 0 || itemsVoidedPrinted.length !== 0
        if (!skipPrinting) await this.printPrintTransaction(printTransaction)

        // Add printed IDS to local storage
        printTransaction.placeOrderItemIds.length !== 0 && addPrintedIds(printTransaction.placeOrderItemIds, true)
        printTransaction.voidOrderItemIds.length !== 0 && addPrintedIds(printTransaction.voidOrderItemIds, false)

        console.log('PRINT SUCCESS, COMMITTING PRINT TRANSACTION')

        await this.commitPrintTransaction(printTransaction.type, printTransaction.placeOrderItemIds, printTransaction.voidOrderItemIds)

        // We have notified the API that printing was successful, we can now remove the printed IDS from local storage.
        printTransaction.placeOrderItemIds.length !== 0 && removePrintedIds(printTransaction.placeOrderItemIds, true)
        printTransaction.voidOrderItemIds.length !== 0 && removePrintedIds(printTransaction.voidOrderItemIds, false)
      } catch (errorMessage) {
        console.log('Error while printing print transaction: ', errorMessage)
      }
    })

    this.commands.handlePrintDataComplete?.(
      _(printTransactions).map('placeOrderItemIds').flatten().value(),
      _(printTransactions).map('voidOrderItemIds').flatten().value()
    )
  }

  printPrintTransaction (printTransaction) {
    return new Promise((resolve, reject) => {
      const printResult = this.commands.handlePrintData?.({ receipt_commands: JSON.parse(printTransaction.receiptData || '[]') })

      if (printResult.error) {
        Notify.create({
          message: printResult.message,
          type: 'negative',
          position: 'top'
        })

        reject(printResult.message)
      }

      resolve()
    })
  }

  async commitPrintTransaction (type, placeOrderItemIds, voidOrderItemIds) {
    console.log(`COMMITTING PRINT TRANSACTION: ${type} ${placeOrderItemIds} ${voidOrderItemIds}`)

    this.apolloClient.mutate({
      mutation: gql`mutation ($input: CommitPrintTransactionInput!){
            commitPrintTransaction(input: $input) {
              errors {
                code
                description
              }
            }
          }`,
      variables: {
        input: {
          printTransactionInput: {
            type,
            placeOrderItemIds,
            voidOrderItemIds
          }
        }
      }
    }).catch((error) => {
      console.log('Error while committing print transaction: ', error.message)
    })
  }
}

/**
 * @param {import('@quasar/app-webpack').BootFileParams} params
 */
export default async ({ app }) => {
  if (!app.config.globalProperties.$cable || (!isKds && !isPos) || !isElectronMode) return

  const printTransactionsService = new PrintTransactionsService(app.apolloProvider.defaultClient)

  printTransactionsService.connect()

  app.config.globalProperties.$printPickPrep = (orderId, callback) => printTransactionsService.printPickPrep(orderId, callback)
  app.config.globalProperties.$printTransactionsCommands = printTransactionsService.commands
}

let placedPrintedIds = []
let voidedPrintedIds = []

export function getPrintedIds (placed = true) {
  return placed ? placedPrintedIds : voidedPrintedIds
}

export function addPrintedIds (ids, placed = true) {
  if (placed) {
    placedPrintedIds.push(...ids)
  } else {
    voidedPrintedIds.push(...ids)
  }
}

export function removePrintedIds (ids, placed = true) {
  if (placed) {
    placedPrintedIds = placedPrintedIds.filter(id => !ids.includes(id))
  } else {
    voidedPrintedIds = voidedPrintedIds.filter(id => !ids.includes(id))
  }
}
